import * as THREE from 'three';
import { cloth, emis, leather, metal, std } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { figure, skirt } from './heroes.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The Circle's wood under the Grandsire (docs/sendas/druida.md, chapter 1): its people and the Lodazal, painted
 * miniatures.
 *
 * - Ceda: thirty-eight, strong, of the country; a dress of dark green wool under a shawl of moss, her sleeves rolled,
 *   a belt with a pouch of seeds; her hair brown in one thick braid with a sprig in it; a staff of ash that puts out
 *   leaves (pale new green at its knots and tip).
 * - Hob: the collier, broad, soot to the elbows; a shirt gone grey with charcoal dust under a heavy leather apron, a
 *   felt cap, a short dark beard, soot on his cheeks; a sack of charcoal over one shoulder and a hatchet at his belt.
 * - Espino: seventy, tall and thin as a stick, a long robe of grey-green with its hood down, thin white hair and a thin
 *   long beard, gaunt; the curved knife for mistletoe in his right hand, its blade pale, a sprig of mistletoe (pale
 *   leaves, white berries) in his left.
 * - The Lodazal: a slime the size of a cart made of the flood's mud, lumps of it run together, wet and shining, black
 *   roots sticking out of it, two pale sunken eyes and a mouth that hangs open; it drips.
 *
 * Colors: the wood's greens, bark and earth, the soot's black; never gold. The Lodazal's eyes cold, not red.
 */

const SKIN = '#c9a08a';

/** Ceda: the staff that puts out leaves, the shawl of moss, the braid. */
const ceda = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 901);
  const dress = cloth('#36452f');
  const shawl = cloth('#5b7a3c');
  const skin = std(SKIN, { roughness: 0.86 });
  const f = figure({ legs: cloth('#2f3a29'), boots: leather('#2a211b'), torso: dress, shoulders: shawl, arms: cloth('#40513a'), forearms: skin, hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(1.0, 0.98, 1.0);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The skirt to the ground, a darker hem.
  skirt(f, std('#36452f', { side: THREE.DoubleSide, roughness: 0.95 }), 0.31, 0.86);
  // The shawl of moss over the shoulders, its fringe, its knot at the chest.
  const moss = std('#6a8a44', { roughness: 1, flatShading: true });
  for (let i = 0; i < 11; i++) {
    const a = ((i - 5) / 5) * 2.0 + Math.PI;
    const tuft = M(new THREE.DodecahedronGeometry(0.05, 0), i % 3 ? moss : shawl, b);
    tuft.position.set(Math.sin(a) * 0.19, 1.43 - Math.abs(i - 5) * 0.012, Math.cos(a) * 0.17);
    tuft.scale.set(1.2, 0.6, 0.8);
  }
  M(new THREE.DodecahedronGeometry(0.04, 0), moss, b).position.set(0, 1.36, 0.17);
  // The belt and the pouch of seeds.
  ring(b, 0.17, 0.02, leather('#3a2c22'), 0.98, 4, 14);
  const pouch = M(new THREE.SphereGeometry(0.06, 8, 6), leather('#4a3a2c'), b);
  pouch.position.set(0.17, 0.92, 0.09);
  pouch.scale.set(1, 1.2, 0.8);
  // Her hair: brown, one thick braid down her back with a sprig at its tie.
  const hair = std('#4a3426', { roughness: 1 });
  const top = M(new THREE.SphereGeometry(0.135, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.58), hair, h);
  top.position.set(0, 1.655, -0.01);
  top.scale.set(1.05, 1, 1.1);
  for (let i = 0; i < 5; i++) {
    const k = M(new THREE.SphereGeometry(0.045 - i * 0.004, 7, 5), hair, h);
    k.position.set(0, 1.55 - i * 0.075, -0.15 - i * 0.012);
  }
  const sprig = M(new THREE.ConeGeometry(0.022, 0.09, 4), std('#9ccf6a', { roughness: 0.8 }), h);
  sprig.position.set(0.03, 1.2, -0.21);
  sprig.rotation.z = 0.6;
  // Her face: dark eyes, a straight nose, ruddy cheeks.
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#18140f'), h).position.set(k * 0.043, 1.656, 0.114);
    M(new THREE.BoxGeometry(0.046, 0.011, 0.014), hair, h).position.set(k * 0.045, 1.684, 0.11);
    M(new THREE.SphereGeometry(0.022, 6, 4), std('#c98a7a', { roughness: 0.9 }), h).position.set(k * 0.07, 1.62, 0.1);
  }
  const nose = M(new THREE.ConeGeometry(0.017, 0.045, 5), std('#c0947e', { roughness: 0.85 }), h);
  nose.position.set(0, 1.628, 0.13);
  nose.rotation.x = Math.PI / 2 + 0.2;
  // The staff of ash, held upright, putting out leaves at its knots and its tip.
  const staff = G(f.rhAbs);
  const bark = std('#5a4a3a', { roughness: 1 });
  const shaft = [V(0.3, 0.02, 0.12), V(0.31, 0.75, 0.11), V(0.295, 1.45, 0.125), V(0.305, 1.95, 0.12)];
  for (let i = 0; i < 3; i++) limb(staff, shaft[i] as THREE.Vector3, shaft[i + 1] as THREE.Vector3, 0.024 - i * 0.002, 0.022 - i * 0.002, bark, 6);
  const leaf = std('#a6d873', { roughness: 0.7, emissive: '#2c4a14', emissiveIntensity: 0.6 });
  const leafs: THREE.Mesh[] = [];
  for (const [y, n] of [
    [0.75, 2],
    [1.45, 2],
    [1.95, 5],
  ] as const) {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + y;
      const l = M(new THREE.ConeGeometry(0.03, 0.11, 4), leaf, staff);
      l.position.set(0.3 + Math.cos(a) * 0.04, y + 0.03 + (n > 2 ? 0.03 : 0), 0.12 + Math.sin(a) * 0.04);
      l.rotation.set(Math.sin(a) * 0.9, 0, -Math.cos(a) * 0.9);
      l.scale.z = 0.35;
      leafs.push(l);
    }
  }
  glowSprite(staff, '#c6f0a0', 0.22, V(0.305, 2.0, 0.12), 0.5);
  f.rig.armR.rotation.x = -0.25;
  const tick = (n: number): void => {
    leafs.forEach((l, i) => (l.rotation.y = Math.sin(n * 0.8 + i) * 0.25));
  };
  return done(c, 1.95, tick, f.rig);
};

/** Hob: soot to the elbows, the apron, the cap, the sack of charcoal, the hatchet. */
const hob = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 902);
  const shirt = cloth('#4a4b4f');
  const soot = std('#2a2a2c', { roughness: 1 });
  const skin = std('#b89280', { roughness: 0.88 });
  const f = figure({ legs: cloth('#2e2c2a'), boots: leather('#1e1915'), torso: shirt, shoulders: shirt, arms: shirt, forearms: soot, hands: soot, skin });
  c.g.add(f.fig);
  // Broad: a collier's shoulders.
  f.fig.scale.set(1.12, 1.0, 1.1);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The heavy apron, scorched, its strap.
  const apron = leather('#3b2b20');
  const ap = M(new THREE.BoxGeometry(0.36, 0.72, 0.022), apron, b);
  ap.position.set(0, 1.02, 0.2);
  ap.rotation.x = -0.05;
  M(new THREE.BoxGeometry(0.36, 0.05, 0.024), std('#1a1410', { roughness: 1 }), b).position.set(0, 0.68, 0.215);
  for (const k of [-1, 1]) {
    const st = M(new THREE.BoxGeometry(0.03, 0.34, 0.02), apron, b);
    st.position.set(k * 0.1, 1.36, 0.16);
    st.rotation.z = k * 0.25;
  }
  // The felt cap, flat, pulled down.
  const cap = M(new THREE.CylinderGeometry(0.15, 0.145, 0.06, 12), cloth('#2f3033'), h);
  cap.position.set(0, 1.76, 0);
  cap.rotation.x = -0.12;
  const brim = M(new THREE.CylinderGeometry(0.17, 0.17, 0.012, 12), cloth('#26272a'), h);
  brim.position.set(0, 1.735, 0.03);
  brim.rotation.x = -0.15;
  // A short dark beard, soot on his cheeks, small dark eyes under heavy brows.
  const beard = std('#2a2220', { roughness: 1 });
  const bd = M(new THREE.SphereGeometry(0.1, 10, 6, 0, Math.PI * 2, Math.PI * 0.45, Math.PI * 0.55), beard, h);
  bd.position.set(0, 1.62, 0.04);
  bd.scale.set(1.05, 1, 1.1);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.011, 5, 4), std('#141214'), h).position.set(k * 0.043, 1.662, 0.114);
    M(new THREE.BoxGeometry(0.052, 0.014, 0.016), beard, h).position.set(k * 0.045, 1.69, 0.11);
    const smear = M(new THREE.SphereGeometry(0.03, 6, 4), soot, h);
    smear.position.set(k * 0.075, 1.64, 0.088);
    smear.scale.set(1, 0.6, 0.4);
  }
  const nose = M(new THREE.ConeGeometry(0.02, 0.05, 5), std('#ad8774', { roughness: 0.85 }), h);
  nose.position.set(0, 1.63, 0.13);
  nose.rotation.x = Math.PI / 2 + 0.2;
  // The sack of charcoal over his left shoulder, lumps showing at its mouth.
  const sack = M(new THREE.SphereGeometry(0.17, 9, 7), cloth('#5a5248'), b);
  sack.position.set(-0.2, 1.48, -0.12);
  sack.scale.set(0.9, 1.15, 0.85);
  for (let i = 0; i < 4; i++) M(new THREE.DodecahedronGeometry(0.035, 0), std('#151516', { roughness: 0.6, metalness: 0.1 }), b).position.set(-0.17 + (i % 2) * 0.05, 1.66, -0.1 + (i >> 1) * 0.05);
  // The hatchet at his belt.
  ring(b, 0.172, 0.02, leather('#2a1f18'), 0.95, 4, 14);
  const ax = G(b, 0.2, 0.86, 0.06);
  limb(ax, V(0, 0, 0), V(0, 0.3, 0), 0.014, 0.014, std('#4a3a2c', { roughness: 1 }), 5);
  const head = M(new THREE.BoxGeometry(0.09, 0.06, 0.02), metal('#5e646c', { roughness: 0.45 }), ax);
  head.position.set(0.035, 0.27, 0);
  f.rig.armL.rotation.set(-0.3, 0, 0.5);
  return done(c, 1.78, () => undefined, f.rig);
};

/** Espino: tall and thin, the long robe, the curved knife and the mistletoe. */
const espino = (o?: BaseOpts): Model => {
  const c = begin(o, 0.58, 903);
  const robe = cloth('#4a5348');
  const skin = std('#c2a596', { roughness: 0.9 });
  const f = figure({ legs: robe, boots: leather('#2a231e'), torso: robe, shoulders: robe, arms: robe, forearms: robe, hands: skin, skin });
  c.g.add(f.fig);
  // Tall and thin as a stick.
  f.fig.scale.set(0.86, 1.1, 0.86);
  const b = f.bodyAbs;
  const h = f.headAbs;
  skirt(f, std('#4a5348', { side: THREE.DoubleSide, roughness: 0.95 }), 0.29, 0.96);
  // The hood down on his back, a cord belt with dried herbs.
  const hood = M(new THREE.SphereGeometry(0.13, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.6), cloth('#3c443a'), b);
  hood.position.set(0, 1.42, -0.16);
  hood.rotation.x = 1.2;
  ring(b, 0.16, 0.012, std('#6a6250', { roughness: 1 }), 0.98, 3, 14);
  for (let i = 0; i < 3; i++) {
    const herb = M(new THREE.ConeGeometry(0.02, 0.14, 4), std('#6d7a4a', { roughness: 1 }), b);
    herb.position.set(-0.12 + i * 0.05, 0.9, 0.12);
    herb.rotation.x = Math.PI;
  }
  // Thin white hair, a thin long beard, deep-set eyes, a long nose.
  const white = std('#d6d8d4', { roughness: 1 });
  const top = M(new THREE.SphereGeometry(0.13, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.4), white, h);
  top.position.set(0, 1.66, -0.02);
  const beard = M(new THREE.ConeGeometry(0.06, 0.32, 7), white, h);
  beard.position.set(0, 1.47, 0.09);
  beard.rotation.x = Math.PI + 0.12;
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.011, 5, 4), std('#121212'), h).position.set(k * 0.04, 1.655, 0.11);
    M(new THREE.BoxGeometry(0.05, 0.012, 0.016), white, h).position.set(k * 0.044, 1.685, 0.108);
    const hollow = M(new THREE.SphereGeometry(0.022, 6, 4), std('#a48a7e', { roughness: 0.9 }), h);
    hollow.position.set(k * 0.06, 1.6, 0.09);
    hollow.scale.set(1, 1.3, 0.4);
  }
  const nose = M(new THREE.ConeGeometry(0.016, 0.06, 5), std('#b89a8c', { roughness: 0.85 }), h);
  nose.position.set(0, 1.625, 0.135);
  nose.rotation.x = Math.PI / 2 + 0.25;
  // The curved knife in his right hand: a horn grip, a pale sickle blade.
  const knife = G(f.rhAbs, 0.29, 0.86, 0.14);
  limb(knife, V(0, 0, 0), V(0, 0.12, 0.02), 0.014, 0.012, std('#3a302a', { roughness: 0.8 }), 5);
  const blade = M(new THREE.TorusGeometry(0.09, 0.012, 3, 10, Math.PI * 0.85), metal('#c9ced6', { roughness: 0.25 }), knife);
  blade.position.set(0.06, 0.16, 0.02);
  blade.rotation.set(0, Math.PI / 2, 0.3);
  blade.scale.z = 0.4;
  // The mistletoe in his left: pale paired leaves and white berries.
  const mist = G(f.lhAbs, -0.29, 0.9, 0.14);
  const ml = std('#9fb880', { roughness: 0.8 });
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const l = M(new THREE.ConeGeometry(0.018, 0.08, 4), ml, mist);
    l.position.set(Math.cos(a) * 0.04, 0.05 + (i % 2) * 0.03, Math.sin(a) * 0.04);
    l.rotation.set(Math.sin(a), 0, -Math.cos(a));
    l.scale.z = 0.4;
  }
  for (let i = 0; i < 5; i++) M(new THREE.SphereGeometry(0.012, 5, 4), emis('#f4f6f2', '#d8e0d0', 0.4), mist).position.set(Math.cos(i * 1.3) * 0.025, 0.07, Math.sin(i * 1.3) * 0.025);
  f.rig.armR.rotation.x = -0.35;
  f.rig.armL.rotation.x = -0.3;
  return done(c, 2.0, () => undefined, f.rig);
};

/**
 * The Lodazal: the flood's mud run together into something alive, the size of a cart. Faceted lumps of wet mud piled
 * up, glossy where the water runs off them, mud dripping off its flanks into the stream; on its back what the flood
 * carried down and it swallowed: a drowned stump's black snags, broken branches, a tuft of reeds, stones, a scrap of
 * moss; two thick arms of mud with stones for knuckles (its slap); a wide face low at its front, two cold pale eyes
 * sunk in it, the mouth hanging open in strands of mud. Read from above: a dark brown hump with green moss and white
 * stones on it, the eyes' cold glow at its front.
 * (Colours of mud and wood, never gold; the eyes cold, never red.)
 */
const lodazal = (o?: BaseOpts): Model => {
  const c = begin(o, 0.95, 904);
  const g = c.g;
  const mud = std('#3d2e22', { roughness: 0.32, metalness: 0.04, flatShading: true });
  const mudDark = std('#2a1f17', { roughness: 0.4, flatShading: true });
  const wet = std('#56422f', { roughness: 0.12, metalness: 0.1, flatShading: true });
  const dark = std('#140f0b', { roughness: 0.7 });
  const root = std('#0f0d0c', { roughness: 0.85, flatShading: true });
  const wood = std('#4a3a2c', { roughness: 0.85, flatShading: true });
  const stone = std('#8a8f92', { roughness: 0.9, flatShading: true });
  const moss = std('#4f7a2e', { roughness: 0.85, flatShading: true });
  const reed = std('#7a8a3a', { roughness: 0.8, flatShading: true });
  const body = G(g);
  // The body: faceted lumps run together, the biggest low, a hump behind, the front rounder.
  const lumps: [number, number, number, number, THREE.Material][] = [
    [0, 0.42, -0.02, 0.62, mud],
    [0.36, 0.3, 0.12, 0.42, mudDark],
    [-0.38, 0.28, 0.08, 0.44, mud],
    [0.06, 0.74, -0.18, 0.44, wet],
    [-0.22, 0.62, -0.32, 0.32, mudDark],
    [0.28, 0.58, -0.3, 0.3, mud],
    [0.18, 0.2, 0.42, 0.3, wet],
    [-0.22, 0.18, 0.4, 0.28, mud],
  ];
  lumps.forEach(([x, y, z, r, m], i) => {
    const l = M(new THREE.IcosahedronGeometry(r, 1), m, body);
    l.position.set(x, y, z);
    l.scale.set(1, 0.8, 1);
    l.rotation.set(i * 0.7, i * 1.3, 0);
  });
  // Glossy runs of water down it.
  for (let i = 0; i < 6; i++) {
    const a = Math.PI * (0.1 + i * 0.32);
    limb(body, V(Math.cos(a) * 0.3, 0.85, Math.sin(a) * 0.3 - 0.1), V(Math.cos(a) * 0.62, 0.25, Math.sin(a) * 0.58), 0.035, 0.02, wet, 4);
  }
  // Mud dripping off its flanks, long strands to the water.
  const drips: THREE.Mesh[] = [];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + 0.2;
    const d = M(new THREE.ConeGeometry(0.045, 0.24, 5), i % 2 ? wet : mud, body);
    d.position.set(Math.cos(a) * 0.6, 0.16, Math.sin(a) * 0.56);
    d.rotation.x = Math.PI;
    drips.push(d);
  }
  // On its back what the flood carried: a drowned stump's snags, broken branches, reeds, stones, moss.
  for (let i = 0; i < 5; i++) {
    const a = Math.PI * 0.2 + (i / 4) * Math.PI * 0.6 + Math.PI;
    const p0 = V(Math.cos(a) * 0.26, 0.82 + (i % 2) * 0.08, Math.sin(a) * 0.26 - 0.1);
    const p1 = V(Math.cos(a) * 0.42, 1.12 + (i % 3) * 0.1, Math.sin(a) * 0.4 - 0.12);
    const p2 = V(Math.cos(a + 0.4) * 0.52, 1.3 + (i % 2) * 0.16, Math.sin(a + 0.4) * 0.56 - 0.12);
    limb(g, p0, p1, 0.07, 0.05, root, 5);
    limb(g, p1, p2, 0.05, 0.012, root, 5);
  }
  for (const [x0, y0, z0, x1, y1, z1] of [
    [-0.4, 0.62, -0.1, -0.85, 0.82, 0.1],
    [0.35, 0.7, -0.15, 0.78, 0.98, -0.32],
    [0.1, 0.85, 0.05, 0.42, 1.0, 0.32],
  ] as const) {
    limb(g, V(x0, y0, z0), V(x1, y1, z1), 0.04, 0.02, wood, 5);
    limb(g, V((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2), V((x0 + x1) / 2 + 0.12, (y0 + y1) / 2 + 0.2, (z0 + z1) / 2 + 0.06), 0.02, 0.008, wood, 4);
  }
  for (let i = 0; i < 7; i++) {
    const r = M(new THREE.ConeGeometry(0.012, 0.42 + (i % 3) * 0.08, 3), reed, g);
    r.position.set(-0.2 + (i % 4) * 0.04, 1.02, -0.36 + Math.floor(i / 4) * 0.05);
    r.rotation.set(-0.3 + (i % 3) * 0.2, 0, -0.4 + i * 0.12);
  }
  for (let i = 0; i < 6; i++) {
    const st = M(new THREE.DodecahedronGeometry(0.07 + (i % 3) * 0.03, 0), stone, g);
    st.position.set(Math.cos(i * 1.9) * 0.48, 0.5 + (i % 2) * 0.24, Math.sin(i * 1.9) * 0.42 - 0.08);
  }
  for (const [x, y, z, sc] of [
    [0.1, 0.98, -0.12, 0.2],
    [-0.36, 0.72, 0.1, 0.16],
    [0.4, 0.62, 0.02, 0.14],
  ] as const) {
    const m = M(new THREE.IcosahedronGeometry(sc, 0), moss, g);
    m.position.set(x, y, z);
    m.scale.y = 0.35;
  }
  // Two arms of mud, thick, stones for knuckles, hanging forward on either side of its face (its slap).
  const arms: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    const arm = G(body, k * 0.48, 0.48, 0.18);
    limb(arm, V(0, 0, 0), V(k * 0.22, -0.12, 0.3), 0.15, 0.12, mud, 6);
    limb(arm, V(k * 0.22, -0.12, 0.3), V(k * 0.18, -0.36, 0.5), 0.12, 0.13, mudDark, 6);
    const fist = M(new THREE.IcosahedronGeometry(0.15, 1), wet, arm);
    fist.position.set(k * 0.18, -0.38, 0.52);
    for (let j = 0; j < 3; j++) M(new THREE.DodecahedronGeometry(0.045, 0), stone, arm).position.set(k * 0.18 + (j - 1) * 0.08, -0.32, 0.64);
    arms.push(arm);
  }
  // The face low at its front: two cold pale eyes sunk deep, heavy brows of mud, the mouth hanging open in strands.
  const face = G(g, 0, 0.5, 0.52);
  for (const k of [-1, 1]) {
    const socket = M(new THREE.SphereGeometry(0.11, 8, 6), dark, face);
    socket.position.set(k * 0.17, 0.14, 0);
    socket.scale.z = 0.5;
    const eye = M(new THREE.SphereGeometry(0.055, 8, 6), emis('#e8f2ee', '#a8d0c0', 1.8), face);
    eye.position.set(k * 0.17, 0.13, 0.035);
    const brow = M(new THREE.IcosahedronGeometry(0.1, 0), mudDark, face);
    brow.position.set(k * 0.18, 0.24, 0.02);
    brow.scale.set(1.5, 0.5, 0.7);
    brow.rotation.z = k * -0.3;
  }
  glowSprite(face, '#c8e0d4', 0.6, V(0, 0.13, 0.08), 0.4);
  const mouth = M(new THREE.SphereGeometry(0.18, 10, 7), dark, face);
  mouth.position.set(0, -0.12, 0);
  mouth.scale.set(1.3, 0.75, 0.5);
  for (let i = 0; i < 4; i++) {
    const st = M(new THREE.CylinderGeometry(0.012, 0.02, 0.2, 4), wet, face);
    st.position.set(-0.12 + i * 0.08, -0.1, 0.06);
  }
  const tick = (n: number): void => {
    body.scale.set(1 + Math.sin(n * 0.9) * 0.04, 1 - Math.sin(n * 0.9) * 0.05, 1 + Math.cos(n * 0.7) * 0.03);
    drips.forEach((d, i) => (d.scale.y = 1 + 0.5 * Math.sin(n * 2 + i)));
    arms.forEach((a, i) => (a.rotation.x = 0.12 * Math.sin(n * 0.8 + i * 1.7)));
  };
  return done(c, 1.5, tick);
};

/** The wood's people and the Lodazal by name (models/sendas.ts sends them here). */
export const bosqueModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'ceda':
      return ceda(o);
    case 'hob':
      return hob(o);
    case 'espino':
      return espino(o);
    case 'lodazal':
      return lodazal(o);
    default:
      return null;
  }
};
